import { Modal } from 'react-bootstrap'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setPreviewData } from 'Manage/actions'
import styles from './PreviewModal.module.css'
import CTAButton from '../CTAButton/CTAButton'
import { onUpgradePlan, convertToBytes, addUpgradeReason, isPDF, formatFileSize, bytesToMB } from 'utils/general'
import CountdownTimer from 'components/CountDownTimer/CountDownTimer'
import { MAX_FREE_FILE_SIZE, linkPreviewTypes, MAX_FREE_PDF_SIZE } from '../../constants'
import { PLANS } from '../../constants/plans'
import { uploadedFileTooBig } from 'utils/sendy'
import { fetchUserData } from 'Manage/actions'
import CustomModal from 'components/CustomModal/CustomModal'
import { deleteSiteFiles } from 'services/site'

export const FILE_TYPE_PDF = 'application/pdf'

const PreviewModal = ({ onHide, show, site, userProfile, liveSitesCount }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const siteLimit = userProfile?.siteLimit ?? 1
  const [isOpen, setIsOpen] = useState(true)

  React.useEffect(() => {
    if (show && window.location.pathname !== '/manage/preview') {
      history.push('/manage/preview')
    }
  }, [show, history])

  const handleOnHide = async () => {
    history.push({ pathname: '/manage' })
    dispatch(setPreviewData(undefined))
    if (site?.linkPreviewType === linkPreviewTypes.FILE_SIZE) {
      uploadedFileTooBig()
    }
    if (siteLimit <= liveSitesCount && reachedProjectLimit(site)) {
      try {
        await deleteSiteFiles({
          domain: site?.subdomain || site?.link,
          site
        })
        dispatch(fetchUserData(false))
      } catch (err) {
        console.error('Delete Unsuccessful', err)
      }
    }
    onHide()
  }

  const onClose = async () => {
    await handleOnHide()
  }

  const toggleAccordion = () => {
    setIsOpen(!isOpen)
  }

  const onPublish = (site) => {
    if (site?.linkPreviewType === 'VIDEO_FILE') {
      addUpgradeReason('Video File Upload')
      onUpgradePlan({ planId: PLANS.PRO_U.id }, dispatch)
    } else if (reachedProjectLimit(site)) {
      addUpgradeReason('Project Limit')
      onUpgradePlan({ projects: siteLimit + 1 }, dispatch)
    } else {
      addUpgradeReason('Preview Mode')
      const fileSize = site?.size ? site?.size : convertToBytes(MAX_FREE_FILE_SIZE)
      onUpgradePlan({ fileSize: fileSize }, dispatch)
    }
    onClose()
  }

  const getDomain = (siteRecord) => {
    const subDomain = siteRecord?.subdomain || siteRecord?.link
    const domain = siteRecord || ''
    return subDomain || domain
  }

  const isPHP = (site) => {
    return site && getDomain(site)?.endsWith('tiiny.io')
  }

  const isMalicious = (site) => {
    return site && site.linkPreviewType === linkPreviewTypes.MALICIOUS_CONTENT
  }

  const reachedProjectLimit = (site) => {
    return site && site.linkPreviewType === linkPreviewTypes.PROJECT_LIMIT_REACHED
  }

  const getUpgradeMessage = (site) => {
    const maxSize = site?.type && isPDF(site) ? MAX_FREE_PDF_SIZE : MAX_FREE_FILE_SIZE
    const fileSize = bytesToMB(site?.size)
    if (isPHP(site)) {
      return 'PHP uploads are not available on the free plan'
    } else if (isMalicious(site)) {
      return (
        <>
          This content can't be hosted on the free plan.{' '}
          <a
            href="https://helpdesk.tiiny.host/en/article/were-unable-to-host-this-type-of-content-on-our-free-plan-1l9v94w/"
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </a>
        </>
      )
    } else if (site?.linkPreviewType === 'VIDEO_FILE') {
      return "Video files can't be hosted on the free plan"
    } else if (reachedProjectLimit(site)) {
      return `Your current plan allows ${siteLimit} ${siteLimit === 1 ? 'project' : 'projects'}`
    } else if (fileSize > maxSize || site?.linkPreviewType === linkPreviewTypes.FILE_SIZE) {
      return "Your content is larger than your account's limit"
    } else {
      return ''
    }
  }

  const getFileSizeInfo = (site) => {
    const maxSize = site?.type && isPDF(site) ? MAX_FREE_PDF_SIZE : MAX_FREE_FILE_SIZE
    const fileSize = bytesToMB(site?.size)

    if (
      (fileSize > maxSize || site?.linkPreviewType === linkPreviewTypes.FILE_SIZE) &&
      !isMalicious(site) &&
      !isPHP(site)
    ) {
      return (
        <span className={styles.fileSizeInfo}>
          {site?.size ? (
            <>
              <span className={styles.redText}>{formatFileSize(site?.size)}</span>
              {' / '}
            </>
          ) : null}
          {`${maxSize} MB ${site?.type === FILE_TYPE_PDF ? '(PDF limit)' : 'limit'}`}
        </span>
      )
    }
    return null
  }

  const fileSizeInfo = getFileSizeInfo(site)

  return (
    <CustomModal
      size="xl"
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      enforceFocus={false}
    >
      <Modal.Body className={styles.body}>
        <iframe className={styles.iframe} src={`https://${getDomain(site)}`} title="Link Preview" allowFullScreen />
      </Modal.Body>
      <div className={`${styles.container}`}>
        <div className={styles.dropdown} onClick={toggleAccordion}>
          <img src={isOpen ? '/assets/icons/down-arrow.svg' : '/assets/icons/up-arrow.svg'} alt="toggle" />
        </div>

        {isOpen ? (
          <div className={styles.open}>
            <div className={styles.contents}>
              <div className={`${styles.content} ${styles.content1}`}>
                <h1 className={styles.heading} data-testid="PreviewModal-div-link-preview">
                  Project Preview
                </h1>
                <div className={styles.timer}>{site?.created && <CountdownTimer creationTime={site.created} />}</div>
              </div>

              <div className={`${styles.content} ${styles.content2}`}>
                <div className={styles.subHeading}>
                  <span className={styles.subTitle}>{getUpgradeMessage(site)}</span>
                  {fileSizeInfo && <div className={styles.fileSizeInfo}>{fileSizeInfo}</div>}
                </div>
              </div>

              <div className={`${styles.content} ${styles.content3}`}>
                <div className={styles.btnCon}>
                  <CTAButton
                    className={styles.ctaBtn}
                    onClick={() => onPublish(site)}
                    label="Upgrade to publish your link"
                    variant="primary"
                  />
                </div>
                <div className={styles.guarantee}>7-day money-back guarantee | 800k happy users</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.closed}>
            <div className={styles.contentCollapsed}>
              <CountdownTimer creationTime={site?.created} />
              <h2 className={styles.heading2}>Project Preview</h2>
              <div className={styles.btnCon}>
                <CTAButton
                  className={styles.ctaBtn}
                  onClick={() => onPublish(site)}
                  label="Upgrade to publish your link"
                  variant="primary-gradient-round"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  )
}

export default PreviewModal
